import { Component, forwardRef, input } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { Country, MobileValue } from './mobile-input.model';

@Component({
  selector: 'app-mobile-input',
  standalone: true,
  imports: [ReactiveFormsModule, CommonModule, FormsModule],
  templateUrl: './mobile-input.html',
  styleUrls: ['./mobile-input.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MobileInput),
      multi: true,
    },
  ],
})
export class MobileInput implements ControlValueAccessor {
  private isValidEgyptianMobile(num: string): boolean {
    return /^1[0125]\d{8}$/.test(num);
  }

  countries: Country[] = [
    { name: 'Egypt', code: 'eg', dialCode: '+20', flag: '🇪🇬' },
    { name: 'Saudi Arabia', code: 'sa', dialCode: '+966', flag: '🇸🇦' },
    { name: 'UAE', code: 'ae', dialCode: '+971', flag: '🇦🇪' },
    { name: 'United States', code: 'us', dialCode: '+1', flag: '🇺🇸' },
    { name: 'United Kingdom', code: 'gb', dialCode: '+44', flag: '🇬🇧' },
  ];

  selectedCountry: Country = this.countries[0];
  numberValue: string = '';
  error = input<boolean>(false);

  onChange = (value: MobileValue) => {};
  onTouched = () => {};

  writeValue(value: MobileValue | null): void {
    if (!value) {
      this.numberValue = '';
      return;
    }
    this.numberValue = value.number;
    const country = this.countries.find((c) => c.dialCode == value.dialCode);
    if (country) {
      this.selectedCountry = country;
    }
  }

  registerOnChange(fn: (value: MobileValue) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {}

  onInputChange(event: Event) {
    const target = event.target as HTMLInputElement;
    const cursorPos = target.selectionStart ?? 0;
    let digits = target.value.replace(/\D/g, '');

    if (this.selectedCountry.dialCode === '+20') {
      digits = this.normalizeEgyptianNumber(digits) ?? digits;
    }

    this.numberValue = digits;
    target.value = digits;
    const newCursor = Math.min(cursorPos, digits.length);
    target.setSelectionRange(newCursor, newCursor);

    this.onChange({
      dialCode: this.selectedCountry.dialCode,
      number: this.numberValue,
    });

    this.onTouched();
  }
  private normalizeEgyptianNumber(input: string): string | null {
    // Case 1: 11 digits starting with 0 → strip 0
    if (input.length === 11 && input.startsWith('0')) {
      const normalized = input.slice(1);
      return this.isValidEgyptianMobile(normalized) ? normalized : null;
    }

    // Case 2: 10 digits starting with 1
    if (input.length === 10) {
      return this.isValidEgyptianMobile(input) ? input : null;
    }

    return null;
  }

  onCountryChange() {
    this.onChange({ dialCode: this.selectedCountry.dialCode, number: this.numberValue });
    this.onTouched();
  }
}
